import React, { FC, ReactNode } from 'react'
import cx from 'classnames'
import styles from './LifestyleImageGrid.module.scss'
import { HP } from 'components/basic/HP/HP'
import { NumText } from 'components/NumText/NumText'
import { getUnitForRegion } from 'constants/units'
import { RegionShort } from 'utils/internationalization'
import { ImgBackground } from 'components/basic/Img/ImgBackground'
import { Img } from 'components/basic/Img'
import { IconMap } from 'components/_const_assets/icons'
import { AnimateOnReveal } from 'components/AnimateOnScroll'

interface LifestyleImageGridProps extends ImagelessProps {
	image1: string
	image2: string
	image3: string
	icons?: boolean
	animateOnScroll?: boolean
}

interface ImagelessProps {
	title?: ReactNode
	paragraphs?: string[]
	region?: RegionShort
	icons?: boolean
	children?: ReactNode
	animateOnScroll?: boolean
}

/**
 * @deprecated Sunsetting this component.
 */
export const LifestyleImageGridImages: FC<ImagelessProps> = (props) => {
	return (
		<LifestyleImageGrid
			image1={getUnitForRegion('https://eightsleep.imgix.net/lifestyle_grid_1.jpg?v=1691139996', props.region)}
			image2={'https://eightsleep.imgix.net/8106121-mais2-eightsleep-shooting-barcelona-2023-_2x_2_1.jpg?v=1699312857'}
			image3={'https://eightsleep.imgix.net/hand_a4d86531-d54d-4c6a-8715-08ce2727428a.jpg?v=1658754287'}
			{...props}
		/>
	)
}

/**
 * @deprecated Sunsetting this component.
 */
export const LifestyleImageGrid: FC<LifestyleImageGridProps> = (props) => {
	const hp = (
		<HP
			title={props.title ?? 'Improve your sleep beyond belief'}
			type={'h2'}
			titleClass={cx('tac-hp', styles.numtexttitle)}
			paragraphs={props.paragraphs ?? []}
			paragraphClass={styles.numtextsub}
		/>
	)

	return (
		<div className={styles.container}>
			<div className={styles.content_container}>
				{props.animateOnScroll ? <AnimateOnReveal noMinHeight>{hp}</AnimateOnReveal> : hp}

				{props.icons ? (
					<ul className={styles.icons}>
						<IconItem
							src={IconMap.bed}
							alt={'bed'}
							heading={'Fall asleep faster'}
							description={'Up to 44% faster sleep onset'}
						/>
						<IconItem
							src={IconMap.trending_up}
							alt={'trend arrow up'}
							heading={'Increase your recovery'}
							description={'Up to 19% increase in Heart Rate Variability'}
						/>
						<IconItem
							src={IconMap.moon}
							alt={'moon'}
							heading={'Wake up less'}
							description={'Up to 34% more deep sleep'}
						/>
					</ul>
				) : (
					<NumText
						className={styles.numtext}
						items={[
							{
								num: '34%',
								text: <>More Deep Sleep</>,
								prefix: 'Up to',
							},
							{
								num: '32%',
								text: <>Improved Sleep Quality</>,
								prefix: 'Up to',
							},
							{
								num: '19%',
								text: <>Increase in Recovery</>,
								prefix: 'Up to',
							},
						]}
					/>
				)}
			</div>
			{props.children ?? (
				<div className={cx(styles.image_container)}>
					<ImgBackground
						src={props.image1}
						className={styles.img1}
						alt={`Couple sleeping on Pod 3 with temperature readings for ${getUnitForRegion('55 to 110 degrees Fahrenheit', props.region || '')}`}
						dprHeight={675}
					/>
					<ImgBackground
						src={props.image2}
						className={styles.img2}
						alt={'Man using Eight Sleep app'}
						dprHeight={315}
					/>
					<ImgBackground
						src={props.image3}
						className={styles.img3}
						alt={'Hand refilling the Pod 3 Hub'}
						dprHeight={315}
					/>
				</div>
			)}
		</div>
	)
}

interface IconItemProps {
	src: string
	alt: string
	heading: string
	description: string
}

const IconItem: FC<IconItemProps> = (props) => (
	<li className={styles.icon_item}>
		<Img
			src={props.src}
			alt={props.alt}
		/>
		<h5>{props.heading}</h5>
		<p>{props.description}</p>
	</li>
)
